/// <reference types="@types/google.maps" />
import { GoogleMapDetails } from '@/interfaces/entities/google-map.interface';
import { defineStore } from 'pinia';
import { EventEmitter } from 'events';
import { GoogleMapService } from '@/services/store/google-map.service';
import { ref, computed } from 'vue';
import { GoogleMapSettingsItem } from '@/interfaces/components/google-map-item.interface';

// Define the gm_authFailure function
declare global {
  interface Window {
    gm_authFailure: (error: any) => void;
  }
}

export const useGoogleMapStore = defineStore('GoogleMap', () => {
  const googleMapDetails = ref<GoogleMapDetails>({
    formattedAddress: '',
    latitude: 0,
    longitude: 0,
    placeId: '',
    apiKey: '',
    mapId: '',
    defaultZoomLevel: 0,
    tipText: '',
    eventEmitter: new EventEmitter(),
    addressState: null,
    suburbPostcodeMode: false,
  });

  const googleMapSettingsItem = ref<GoogleMapSettingsItem>({
    apiKey: '',
    mapId: '',
    defaultZoomLevel: 0,
    tipText: '',
  });

  const formattedAddress = computed(() => googleMapDetails.value.formattedAddress);
  const latitude = computed(() => googleMapDetails.value.latitude);
  const longitude = computed(() => googleMapDetails.value.longitude);
  const placeId = computed(() => googleMapDetails.value.placeId);
  const apiKey = computed(() => googleMapDetails.value.apiKey);
  const mapId = computed(() => googleMapDetails.value.mapId);
  const defaultZoomLevel = computed(() => googleMapDetails.value.defaultZoomLevel);
  const tipText = computed(() => googleMapDetails.value.tipText);
  const addressState = computed(() => googleMapDetails.value.addressState);
  const suburbPostcodeMode = computed(() => googleMapDetails.value.suburbPostcodeMode);
  const eventEmitter = computed(() => googleMapDetails.value.eventEmitter);
  const errorEventName = 'googleMapsApiError';

  const setFormattedAddress = (address: string) => {
    googleMapDetails.value.formattedAddress = address;
  };

  const setLatitude = (latitude: number) => {
    googleMapDetails.value.latitude = latitude;
  };

  const setLongitude = (longitude: number) => {
    googleMapDetails.value.longitude = longitude;
  };

  const setPlaceId = (placeId: string) => {
    googleMapDetails.value.placeId = placeId;
  };

  const setApiKey = (apiKey: string) => {
    googleMapDetails.value.apiKey = apiKey;
  };

  const setMapId = (mapId: string) => {
    googleMapDetails.value.mapId = mapId;
  };

  const setDefaultZoomLevel = (defaultZoomLevel: number) => {
    googleMapDetails.value.defaultZoomLevel = Number(defaultZoomLevel);
  };

  const setTipText = (tipText: string) => {
    googleMapDetails.value.tipText = tipText;
  };

  const setSuburbPostcodeMode = (suburbPostcodeMode: boolean) => {
    googleMapDetails.value.suburbPostcodeMode = suburbPostcodeMode;
  };

  const setAddressStateFromAddressComponents = (addressComponents: any) => {
    let addressState = null;
    if (addressComponents?.length > 0) {
      const firstComponent = addressComponents[0];
      // Check which format the components are in
      const shortTextKey = 'short_name' in firstComponent ? 'short_name' : 'shortText';

      for (const component of addressComponents) {
        if (component?.types.includes('administrative_area_level_1')) {
          addressState = component[shortTextKey];
          break;
        }
      }
    }

    googleMapDetails.value.addressState = addressState;
  };

  /**
   * Load the Google Maps API
   * @param apiKey
   */
  const loadGoogleMapsApi = async (): Promise<void> => {
    if (apiKey.value) {
      return new Promise((resolve, reject) => {
        window.gm_authFailure = (error: any) => emitErrorEvent(error);

        if (document.getElementById('google-maps-api')) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.id = 'google-maps-api';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey.value}&libraries=places,marker`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          resolve();
        };
        script.onerror = (error) => {
          console.error('Google Maps API script error:', error);
          emitErrorEvent(error);
          reject(error);
        };
        document.head.appendChild(script);
      });
    }
  };

  const emitErrorEvent = (error: any) => {
    if (!googleMapDetails.value.eventEmitter) {
      googleMapDetails.value.eventEmitter = new EventEmitter();
    }
    googleMapDetails.value.eventEmitter.emit(errorEventName, error);
  };

  const getSCGoogleMapsSettings = async () => {
    googleMapSettingsItem.value = await GoogleMapService.getAllGoogleMapsSettings();
  };

  const setMapDetails = async (scGoogleMapsSettings: GoogleMapSettingsItem) => {
    if (scGoogleMapsSettings) {
      setApiKey(scGoogleMapsSettings.apiKey);
      setMapId(scGoogleMapsSettings.mapId);
      setDefaultZoomLevel(scGoogleMapsSettings.defaultZoomLevel);
      setTipText(scGoogleMapsSettings.tipText);
    }
  };

  return {
    googleMapSettingsItem,
    formattedAddress,
    latitude,
    longitude,
    placeId,
    apiKey,
    mapId,
    defaultZoomLevel,
    tipText,
    addressState,
    suburbPostcodeMode,
    eventEmitter,
    errorEventName,
    setFormattedAddress,
    setLatitude,
    setLongitude,
    setPlaceId,
    setApiKey,
    setMapId,
    setDefaultZoomLevel,
    setTipText,
    setSuburbPostcodeMode,
    setAddressStateFromAddressComponents,
    loadGoogleMapsApi,
    emitErrorEvent,
    getSCGoogleMapsSettings,
    setMapDetails,
  };
});
